import { AnyAction } from '@reduxjs/toolkit'
import { FetchStatusCommon } from 'restApi/types'

export type LineItem = {
  item_type: string
  price: string
  quantity: number
  amount: string
  details: { label: string; price: string }[]
}

type BookingData = {
  adults: number
  available_voucher_balance: string
  can_change: boolean
  can_review: boolean
  cancel_at: string
  cancellation_policy: string
  cancelled_at: string
  charge_at: string
  chargeable: boolean
  children: number
  conversation_id: number
  created_at: string
  currency: string
  customer_service_fee: string
  end_date: string
  end_time: string
  id: number
  paid_out: boolean
  payment_status: string
  preapproved_at: string
  promo_image: {
    medium: string
    card_portrait: string
    thumb: string
    large: string
    hero: string
  }
  refundable: boolean
  last_refundable_date: string
  requested_at: string
  start_date: string
  start_time: string
  status: string
  story_id: number
  storyteller_booking: boolean
  tax_amount: string
  total: string
  total_guests: number
  user_id: number
  deferred_payment_eligible: boolean
  deferred_payment: boolean
  pay_half_later_amount: string
  pay_half_later_date: string
  pay_half_now_amount: string
  payment_pending_charge_at: string
  total_paid: string
  total_remaining: string
  total_to_pay: string
  voucher_amount: string
  dates_available: boolean
  line_items: {
    item_type: string
    price: string
    quantity: number
    amount: string
    details: {
      label: string
      price: string
    }[]
  }[]
  tax_rate_details: [
    {
      name: string
      rate: string
      juris_type: string
    }
  ]
  listing: {
    id: number
    title: string
    description_truncated: string
    price: string
    pricing_suffix: string
    nice_pricing_suffix: string
    street: string
    street2: string
    city: string
    postcode: string
    state: string
    country_id: number
    region_id: number
    region_name: string
    location: {
      name: string
      lat: number
      lng: number
    }
    user_id: number
    promo_image: {
      medium: string
      card_portrait: string
      thumb: string
      large: string
      hero: string
    }
    url: string
    status: string
    review_count: number
    review_score: string
    cancellation_policy: string
    instant_book_enabled: boolean
    service_fee_included: boolean
    created_at: string
    updated_at: string
    description: string
    additional_info: string
    adventure_text: string
    listing_type_id: number
    listing_subcategory_id: number
    concurrent_bookings: boolean
    may_submit: boolean
    on_airbnb: boolean
    booking_window: number
    story_eligible: boolean
    giftable: boolean
    bookable: boolean
    instant_book_eligible: boolean
    same_day_bookings: boolean
    preparation_time_in_days: number
    onboarding: boolean
    max_security_deposit: string
    compliance_number: string
    compliance_number_exempt: boolean
    compliance_number_required: boolean
    currency: string
    tax_exclusive: boolean
    availability_score: number
    added_to_bucket_list: boolean
    capacity: number
    weekend_price: string
    weekend_price_includes_sunday: boolean
    extra_person_fee: string
    extra_child_fee: string
    extra_person_threshold: number
    cleaning_fee: string
    security_deposit: string
    check_in: string
    check_out: string
    bedrooms: number
    beds: number
    bathrooms: number
    pet_friendly: boolean
    minimum_booking_duration: number
    weekend_minimum_booking_duration: number
    amenity_ids: number[]
    outdoor_equipment_details: string
    check_in_instructions: string
    user: {
      id: number
      avatar: {
        thumb: string
        large: string
      }
      first_name: string
      url: string
      location: string
      phone_number: string
      phone_number_verified: boolean
      email_verified: boolean
      facebook_verified: boolean
      listing_count: number
      description: string
    }
    listing_type: {
      id: number
      name: string
      slug: string
      active: boolean
    }
  }
  user: {
    id: number
    fullname: string
    first_name: string
    last_name: string
    avatar_url: string
    url: string
    location: string
    description: string
    instagram: string
    phone_number_verified: boolean
    email_verified: boolean
    facebook_verified: boolean
    review_count: number
    listing_count: number
    email: string
    home_phone_number: string
    confirmed_at: string
    notification_settings: {
      book_again_prompts: boolean
      bucket_list_summary: boolean
      complete_booking_reminder: boolean
      sms_booking_confirmed: boolean
      sms_booking_starting: boolean
      sms_enabled: boolean
      sms_instant_bookings: boolean
      sms_new_assignments: boolean
      sms_new_booking_enquiries: boolean
      sms_new_booking_reminders: boolean
      sms_new_booking_requests: boolean
      sms_new_change_requests: boolean
      sms_new_messages: boolean
      sms_payment_failed: boolean
      sms_preapproved_bookings: boolean
      sms_upcoming_bookings: boolean
      sms_upload_story: boolean
      update_calendar: boolean
    }
    storyteller: boolean
    admin: boolean
    host: boolean
    sign_in_count: number
    profile_complete: boolean
    avatar_uploaded: boolean
    phone_number_verified_at: string
    business_detail_complete: boolean
    no_business_number: boolean
    unread_conversations_count: number
    invited_to_sign_up: boolean
  }
  review: string
  pending_change_request: any
  cancelled_by: any
  vouchers?: [
    {
      id: string
      code: string
    }
  ]
  special_offer: any
}

type BookingState = FetchStatusCommon & {
  data: BookingData
  refund: any
  loading: boolean
  retriedPayment: boolean
  create_pending: boolean
}

const initialState: BookingState = {
  data: null,
  refund: null,
  fetching: false,
  fetched: false,
  updating: false,
  updated: true,
  error: null,
  loading: false,
  retriedPayment: false,
  submitting: false,
  create_pending: false,
}

export default function reducer(
  state = initialState,
  action: AnyAction
): BookingState {
  switch (action.type) {
    case 'BOOKING_FETCH_PENDING': {
      return {
        ...state,
        fetching: true,
      }
    }

    case 'BOOKING_FETCH_REJECTED': {
      return {
        ...state,
        fetching: false,
        error: action.payload,
      }
    }

    case 'BOOKING_FETCH_FULFILLED': {
      return {
        ...state,
        fetching: false,
        fetched: true,
        data: action.payload.data,
      }
    }

    case 'BOOKING_REFUND_FETCH_FULFILLED': {
      return {
        ...state,
        refund: action.payload.data,
      }
    }

    case 'BOOKING_UPDATE_PENDING': {
      return {
        ...state,
        updating: true,
      }
    }

    case 'BOOKING_UPDATE_REJECTED': {
      return {
        ...state,
        updating: false,
        error: action.payload,
      }
    }

    case 'BOOKING_UPDATE_FULFILLED': {
      return {
        ...state,
        updating: false,
        updated: true,
        data: action.payload.data,
      }
    }

    case 'BOOKING_ENQUIRY_PREAPPROVE_FULFILLED': {
      return {
        ...state,
        data: action.payload.data,
      }
    }

    case 'BOOKING_ENQUIRY_ACCEPT_FULFILLED': {
      return {
        ...state,
        data: action.payload.data,
      }
    }

    case 'BOOKING_ENQUIRY_CANCEL_FULFILLED': {
      return {
        ...state,
        data: action.payload.data,
      }
    }

    case 'BOOKING_ENQUIRY_DECLINE_FULFILLED': {
      return {
        ...state,
        data: action.payload.data,
      }
    }

    case 'BOOKING_ENQUIRY_RETRY_PAYMENT_PENDING': {
      return {
        ...state,
        loading: true,
      }
    }

    case 'BOOKING_ENQUIRY_RETRY_PAYMENT_REJECTED': {
      return {
        ...state,
        loading: false,
        error: action.payload.data,
      }
    }

    case 'BOOKING_ENQUIRY_RETRY_PAYMENT_FULFILLED': {
      return {
        ...state,
        loading: false,
        data: action.payload.data,
      }
    }

    case 'BOOKING_PAYMENTS_CREATE_PENDING': {
      return {
        ...state,
        loading: true,
        create_pending: true,
      }
    }

    case 'BOOKING_PAYMENTS_CREATE_FULFILLED': {
      return {
        ...state,
        loading: false,
        create_pending: false,
        data: action.payload.data,
      }
    }

    case 'BOOKING_PAYMENTS_CREATE_REJECTED': {
      return {
        ...state,
        loading: false,
        create_pending: false,
        error: action.payload,
      }
    }

    default: {
      return state
    }
  }
}
